import { useMemo } from 'react';

import { inviteTokenForModel } from '@trello/invitation-tokens';
import { dangerouslyConvertPrivacyString } from '@trello/privacy';

import { useJoinBoardModalFragment } from './JoinBoardModalFragment.generated';
import { useJoinBoardModalGhostFragment } from './JoinBoardModalGhostFragment.generated';
import { useJoinBoardModalInviterFragment } from './JoinBoardModalInviterFragment.generated';

export function useJoinBoardModal(boardId: string) {
  const { data, complete: joinBoardDataComplete } = useJoinBoardModalFragment({
    from: { id: boardId },
    optimistic: true,
  });

  const { idMemberInviter, idMemberGhost } = useMemo<{
    idMemberInviter: string;
    idMemberGhost: string;
  }>(() => {
    const inviteToken = inviteTokenForModel(boardId);
    const [idGhost, idInviter] = inviteToken?.split('-') ?? [];
    return { idMemberGhost: idGhost, idMemberInviter: idInviter };
  }, [boardId]);

  const { data: ghostEmailData, complete: ghostDataComplete } =
    useJoinBoardModalGhostFragment({
      from: { id: idMemberGhost },
      optimistic: true,
    });

  const ghostEmail = ghostEmailData?.email;
  const ghostFullName = ghostEmailData?.fullName;

  const boardOpenCardCount = data?.cards.length;

  const { data: memberInviterData, complete: inviterDataComplete } =
    useJoinBoardModalInviterFragment({
      from: { id: idMemberInviter },
      optimistic: true,
    });

  const inviterName = memberInviterData?.fullName;

  const signupUrl =
    '/signup?' +
    new URLSearchParams({
      reauthenticate: String(true),
      returnUrl: `${location.pathname}?completedInviteSignup=1`,
      email: dangerouslyConvertPrivacyString(ghostEmail) || '',
      fullName: dangerouslyConvertPrivacyString(ghostFullName) || '',
    });

  return {
    loading:
      !inviterDataComplete || !ghostDataComplete || !joinBoardDataComplete,
    signupUrl,
    inviterName,
    idOrganization: data?.idOrganization,
    boardName: data?.name,
    desc: data?.desc,
    descData: data?.descData,
    dateLastActivity: data?.dateLastActivity,
    boardOpenCardCount,
  };
}
